<template>
  <div>
    <v-container fluid>
      <!-- Results Summary -->
      <v-card flat class="mb-3 pa-3" style="background: #f5f5f5;">
        <div class="d-flex align-center">
          <v-icon color="primary" class="mr-2">mdi-database-outline</v-icon>
          <span class="subtitle-2 font-weight-medium">
            Showing {{ rows.length }} of {{ totalCount }} record(s)
            <span v-if="filterMonthFrom || filterMonthTo || filterStatus || filterBranchId || filterAdjId" class="ml-2 caption grey--text">
              (filtered)
            </span>
          </span>
        </div>
      </v-card>

      <v-card flat class="mb-4 pa-3">
        <v-row align="center" no-gutters>
          <v-col cols="12" sm="2" class="mr-2">
            <v-menu v-model="menuMonthFrom" :close-on-content-click="false" offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-text-field v-model="filterMonthFrom" label="Month From" dense hide-details placeholder="2026-06" readonly v-bind="attrs" v-on="on" />
              </template>
              <v-date-picker v-model="filterMonthFrom" type="month" @change="menuMonthFrom = false" />
            </v-menu>
          </v-col>
          <v-col cols="12" sm="2" class="mr-2">
            <v-menu v-model="menuMonthTo" :close-on-content-click="false" offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-text-field v-model="filterMonthTo" label="Month To" dense hide-details placeholder="2026-07" readonly v-bind="attrs" v-on="on" />
              </template>
              <v-date-picker v-model="filterMonthTo" type="month" @change="menuMonthTo = false" />
            </v-menu>
          </v-col>
          <v-col cols="12" sm="2" class="mr-2">
            <v-select v-model="filterStatus" :items="statusOptions" label="Status" dense hide-details clearable />
          </v-col>
          <v-col cols="12" sm="2" class="mr-2">
            <v-select v-model="filterBranchId" :items="branches" item-text="name" item-value="id" label="Branch" dense hide-details clearable @change="onBranchChange" />
          </v-col>
          <v-col cols="12" sm="2" class="mr-2">
            <v-select v-model="filterAdjId" :items="filteredAdjusters" item-text="username" item-value="id" label="Adjuster" dense hide-details clearable :disabled="!filterBranchId && filteredAdjusters.length === 0" />
          </v-col>
          <v-col cols="12" sm="1">
            <v-btn color="primary" small @click="applyFilters">Search</v-btn>
          </v-col>
        </v-row>
      </v-card>

      <v-data-table :headers="tableHeaders" :items="rows" :loading="tableLoading"
        :options.sync="pagination" :server-items-length="totalCount"
        :footer-props="{ itemsPerPageOptions: [20, 50, 100, 250], itemsPerPageText: 'Rows per page' }"
        class="elevation-1">
        <template v-slot:[`item.rowNum`]="{ index }">
          <span class="font-weight-medium">{{ index + 1 + (pagination.page - 1) * pagination.itemsPerPage }}</span>
        </template>
        <template v-slot:[`item.status`]="{ item }">
          <v-chip :color="statusColor(item.status)" small dark>
            <v-icon v-if="item.status === 'PAID'" left small>mdi-check-circle</v-icon>
            {{ item.status }}
          </v-chip>
        </template>
        <template v-slot:[`item.receiptDisplay`]="{ item }">
          <template v-if="item.receipt?.receipt_no">
            {{ item.receipt.receipt_no }}
            <span v-if="item.paidAt" class="grey--text caption">
              ({{ formatShortDate(item.paidAt) }})
            </span>
          </template>
          <span v-else class="grey--text">—</span>
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <v-btn x-small color="primary" class="mr-1" @click="viewReport(item)">Report</v-btn>
          <v-btn v-if="item.status === 'PAID' && item.receipt" x-small color="success" @click="downloadReceipt(item)">Payment Voucher</v-btn>
        </template>
      </v-data-table>
    </v-container>

    <!-- Hidden report container for PDF generation -->
    <div id="print-report" ref="printReport" style="display:none;"></div>

    <!-- Report Preview Dialog -->
    <v-dialog v-model="reportDialog" fullscreen hide-overlay transition="dialog-bottom-transition">
      <v-card>
        <v-toolbar dark color="primary">
          <v-btn icon dark @click="reportDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>{{ reportTitle }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn text dark @click="downloadPdfFromDialog">
            <v-icon left>mdi-file-pdf</v-icon> Download PDF
          </v-btn>
          <v-btn text dark @click="printFromDialog">
            <v-icon left>mdi-printer</v-icon> Print
          </v-btn>
        </v-toolbar>
        <div ref="reportPreview" class="report-preview" v-html="reportHtml"></div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import ClaimMonthlyDataService from "@/services/ClaimMonthlyDataService";
import InsDataService from "@/services/InsDataService";
import SubDeptDataService from "@/services/SubDeptDataService";
import moment from "moment";

export default {
  name: "ClaimsBookkeeping",
  data() {
    return {
      rows: [],
      totalCount: 0,
      tableLoading: false,
      pagination: { page: 1, itemsPerPage: 100 },
      filterMonthFrom: "",
      filterMonthTo: "",
      filterStatus: null,
      filterBranchId: null,
      filterAdjId: null,
      branches: [],
      filteredAdjusters: [],
      allAdjusters: [],
      insurer: [],
      subRefTypes: [],
      menuMonthFrom: false,
      menuMonthTo: false,
      statusOptions: [
        { text: "All Statuses", value: null },
        { text: "PRE-DRAFT", value: "PRE-DRAFT" },
        { text: "DRAFT", value: "DRAFT" },
        { text: "MANAGER_REVIEW", value: "MANAGER_REVIEW" },
        { text: "MANAGER_APPROVED", value: "MANAGER_APPROVED" },
        { text: "PAYROLL_CLERK_REVIEW", value: "PAYROLL_CLERK_REVIEW" },
        { text: "PAYROLL_CLERK_APPROVED", value: "PAYROLL_CLERK_APPROVED" },
        { text: "PAYROLL_MANAGER_REVIEW", value: "PAYROLL_MANAGER_REVIEW" },
        { text: "PAID", value: "PAID" },
        { text: "QUERIED", value: "QUERIED" },
        { text: "REJECTED", value: "REJECTED" },
        { text: "PENDING", value: "PENDING" },
      ],
      reportDialog: false,
      reportHtml: "",
      reportGroup: null,
    };
  },
  computed: {
    tableHeaders() {
      return [
        { text: "#", value: "rowNum", sortable: false, width: "50px" },
        { text: "Month", value: "month", sortable: true },
        { text: "Adjuster", value: "adjuster.fullname", sortable: true },
        { text: "Status", value: "status", sortable: true },
        { text: "Receipt", value: "receiptDisplay", sortable: false },
        { text: "Actions", value: "actions", sortable: false },
      ];
    },
    reportTitle() {
      if (!this.reportGroup) return "Claims Report";
      const adj = this.reportGroup.adjuster?.fullname || this.reportGroup.adjuster?.username || '';
      return `Claims Report — ${adj} — ${this.reportGroup.month}`;
    },
  },
  watch: {
    "pagination.page"() { this.fetchData(); },
    "pagination.itemsPerPage"() { this.pagination.page = 1; this.fetchData(); },
  },
  methods: {
    async fetchData() {
      this.tableLoading = true;
      try {
        const params = {
          page: this.pagination.page,
          size: this.pagination.itemsPerPage || 20,
        };
        if (this.filterMonthFrom) params.monthFrom = this.filterMonthFrom;
        if (this.filterMonthTo) params.monthTo = this.filterMonthTo;
        if (this.filterStatus) params.status = this.filterStatus;
        if (this.filterAdjId) params.adjId = this.filterAdjId;
        if (this.filterBranchId) params.branchId = this.filterBranchId;

        const res = await ClaimMonthlyDataService.bookkeeping(params);
        this.rows = res.data.rows;
        this.totalCount = res.data.count || this.rows.length;
      } catch (e) {
        console.error("Failed to load bookkeeping data", e);
      } finally {
        this.tableLoading = false;
      }
    },
    applyFilters() {
      this.pagination.page = 1;
      this.fetchData();
    },
    onBranchChange(branchId) {
      this.filterAdjId = null;
      if (branchId) {
        ClaimMonthlyDataService.getAdjusters({ branchId }).then(res => {
          this.filteredAdjusters = (res.data || []).map(a => ({
            ...a, username: a.username?.toUpperCase() || ''
          })).sort((a, b) => a.username?.localeCompare(b.username || ''));
        });
      } else {
        this.filteredAdjusters = [];
      }
    },
    async viewReport(item) {
      try {
        const res = await ClaimMonthlyDataService.get(item.id);
        const group = res.data;
        if (!group) return;

        this.reportGroup = group;
        this.reportHtml = this.buildReportHtml(group);
        this.reportDialog = true;
      } catch (e) {
        console.error('Failed to build report', e);
        this.$store.dispatch('setSnackbar', { showing: true, text: 'Failed to load report', color: 'error' });
      }
    },
    async downloadPdfFromDialog() {
      try {
        const group = this.reportGroup;
        if (!group) return;

        // Use the hidden container (no size constraints) instead of the preview div
        const container = this.$refs.printReport;
        container.innerHTML = this.buildReportHtml(group);
        container.style.display = 'block';

        // Allow DOM to render
        await new Promise(r => setTimeout(r, 300));

        const html2pdf = (await import('html2pdf.js')).default;
        await html2pdf().set({
          margin: [5, 5, 5, 5],
          filename: `Claims_Report_${group.month}_${group.adjuster?.username || 'adjuster'}.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true, letterRendering: true },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'landscape' },
          pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
        }).from(container).save();
      } catch (e) {
        console.error('PDF generation failed', e);
        this.$store.dispatch('setSnackbar', { showing: true, text: 'PDF generation failed', color: 'error' });
      } finally {
        const container = this.$refs.printReport;
        if (container) {
          container.style.display = 'none';
          container.innerHTML = '';
        }
      }
    },
    printFromDialog() {
      const previewEl = this.$refs.reportPreview;
      if (!previewEl) return;
      const win = window.open('', '_blank');
      if (!win) {
        window.print();
        return;
      }
      win.document.write(`
        <html><head><title>Claims Report</title></head>
        <body>${previewEl.innerHTML}</body></html>
      `);
      win.document.close();
      setTimeout(() => { win.print(); }, 500);
    },
    async downloadReceipt(item) {
      if (!item.receipt?.receipt_no) return;
      try {
        const res = await ClaimMonthlyDataService.get(item.id);
        const group = res.data;
        const win = window.open('', '_blank');
        win.document.write(this.buildReceiptHtml(item, group));
        win.document.close();
        setTimeout(() => { win.print(); }, 500);
      } catch (e) {
        console.error('Failed to generate receipt', e);
      }
    },
    buildReceiptHtml(item, group) {
      const adj = (item.adjuster?.fullname || item.adjuster?.username || '').toUpperCase();
      const rcp = item.receipt;
      const mgrName = (group?.managerUser?.fullname || group?.managerUser?.username || '—').toUpperCase();
      const pmName = (group?.payrollManagerUser?.fullname || group?.payrollManagerUser?.username || '—').toUpperCase();
      return `
        <html><head><title>Receipt ${rcp.receipt_no}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; }
          .header { text-align: center; margin-bottom: 30px; }
          .header h1 { font-size: 18px; margin: 0; }
          .header h2 { font-size: 14px; color: #666; margin: 5px 0; }
          table { width: 100%; border-collapse: collapse; margin: 20px 0; }
          th, td { border: 1px solid #ccc; padding: 8px; text-align: left; }
          th { background: #f5f5f5; }
          .right { text-align: right; }
          .total-row { font-weight: bold; background: #FCE4D6; }
          .footer { margin-top: 30px; text-align: center; font-size: 11px; color: #999; }
        </style></head><body>
        <div class="header">
          <h1>ASSOCIATED ADJUSTERS SDN BHD</h1>
          <h2>OFFICIAL PAYMENT VOUCHER</h2>
          <p>Receipt No: ${rcp.receipt_no} | Date: ${moment(rcp.paid_at).format('DD-MMM-YYYY')}</p>
          <p>Adjuster: ${adj} | Month: ${item.month}</p>
        </div>
        <table>
          <tr><th>Description</th><th class="right">Amount (RM)</th></tr>
          <tr><td>Total Claim Amount</td><td class="right">${this.fmt(item.paidAmount || 0)}</td></tr>
          <tr class="total-row"><td>PAID AMOUNT</td><td class="right">${this.fmt(rcp.paid_amount || 0)}</td></tr>
        </table>
        <div class="footer">
          <p>APPROVED BY</p>
          <p>Manager: ${mgrName}</p>
          <p>Payroll Manager: ${pmName} | ${moment(rcp.paid_at).format('DD-MMM-YYYY')}</p>
          <p>* This is a system-generated voucher. Not an official tax invoice.</p>
        </div>
        </body></html>`;
    },
    buildReportHtml(group) {
      console.log('[buildReportHtml] Group sectionAClaims:', {
        count: group.sectionAClaims?.length,
        claims: group.sectionAClaims?.map(c => ({
          casefileId: c.casefile?.id,
          caseId: c.caseId,
          createdAt: c.createdAt
        }))
      });
      const adj = (group.adjuster?.fullname || group.adjuster?.username || '').toUpperCase();
      const mgrName = (group.managerUser?.fullname || group.managerUser?.username || '—').toUpperCase();
      const clerkName = (group.payrollClerkUser?.fullname || group.payrollClerkUser?.username || '—').toUpperCase();
      const pmName = (group.payrollManagerUser?.fullname || group.payrollManagerUser?.username || '—').toUpperCase();

      // Status stamp
      const stampColors = {
        PAID: { color: '#2E7D32', text: 'PAID' },
        DRAFT: { color: '#E65100', text: 'DRAFT' },
        MANAGER_REVIEW: { color: '#E65100', text: 'PENDING MANAGER' },
        MANAGER_APPROVED: { color: '#E65100', text: 'MANAGER APPROVED' },
        PAYROLL_CLERK_REVIEW: { color: '#E65100', text: 'PENDING PAYROLL CLERK' },
        PAYROLL_CLERK_APPROVED: { color: '#E65100', text: 'CLERK APPROVED' },
        PAYROLL_MANAGER_REVIEW: { color: '#E65100', text: 'PENDING PAYROLL MANAGER' },
        QUERIED: { color: '#E65100', text: 'QUERIED' },
        REJECTED: { color: '#C62828', text: 'REJECTED' },
      };
      const st = stampColors[group.status] || { color: '#E65100', text: group.status };
      const words = st.text.split(' ');
      const mid = Math.ceil(words.length / 2);
      const line1 = words.slice(0, mid).join(' ');
      const line2 = words.slice(mid).join(' ');
      const stampSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="200" height="70" viewBox="0 0 200 70">
        <rect x="8" y="8" width="184" height="54" rx="10" ry="10" fill="none" stroke="${st.color}" stroke-width="4" />
        <text x="100" y="32" font-family="Arial, sans-serif" font-size="16" font-weight="bold" fill="${st.color}" text-anchor="middle" letter-spacing="2">${line1}</text>
        <text x="100" y="50" font-family="Arial, sans-serif" font-size="16" font-weight="bold" fill="${st.color}" text-anchor="middle" letter-spacing="2">${line2}</text>
      </svg>`;
      const statusStamp = `<div style="position:absolute;top:0;right:0;pointer-events:none;z-index:9999;opacity:0.3;">${stampSvg}</div>`;

      // Resolve reference data for TYPE and INSURER
      const depts = this.$store?.state?.referenceData?.departments || [];
      const subs = this.subRefTypes || [];

      // Sort claims by invoice number (case-insensitive, nulls last)
      let claims = (group.sectionAClaims || []).slice().sort((a, b) => {
        const invA = (a.invoiceNo || a.casefile?.invNo || '').toString().toUpperCase();
        const invB = (b.invoiceNo || b.casefile?.invNo || '').toString().toUpperCase();
        return invA.localeCompare(invB);
      });
      const secB = group.sectionBRows || [];
      const secC = group.sectionCRows || [];
      const accomRow = secC.find(r => r.rowType === 'accomodation') || {};

      // Build Section A rows with full 18 columns
      let claimRows = '';
      let colSums = [0,0,0,0,0,0,0,0,0,0]; // invAmt, adjFees, trav, add, sd, med, pol, jpj, misc, typ
      claims.forEach((c, i) => {
        const dept = depts.find(d => Number(d.id) === Number(c.casefile?.refType));
        const sub = subs.find(s => Number(s.id) === Number(c.casefile?.subRefType));
        const deptName = dept?.name || '';
        const subCode = sub?.subCode || '';
        const typeStr = subCode ? `${deptName}/${subCode}` : deptName;
        const ins = this.insurer?.find(i => Number(i.id) === Number(c.casefile?.insurer || c.casefile?.inssId));
        const insRaw = ins?.insCode || ins?.name || '';
        const insName = insRaw.length > 25 ? insRaw.substring(0, 22) + '...' : insRaw;
        const invNo = c.invoiceNo || c.casefile?.invNo || '';
        const invAmt = parseFloat(c.invoiceTotal || c.casefile?.invTotal || 0);
        const adjFees = parseFloat(c.feeNote || c.casefile?.invFeeNote || 0);
        const trav = parseFloat(c.travMileage || 0);
        const add = parseFloat(c.additional || 0);
        const sd = parseFloat(c.sdAndStamps || 0);
        const med = parseFloat(c.medicalReport || 0);
        const pol = parseFloat(c.policeDoc || 0);
        const jpj = parseFloat(c.jpjDoc || 0);
        const misc = parseFloat(c.misc || 0);
        const typ = parseFloat(c.typing || 0);
        const total = trav + add + sd + med + pol + jpj + misc + typ;
        colSums[0] += invAmt; colSums[1] += adjFees; colSums[2] += trav;
        colSums[3] += add; colSums[4] += sd; colSums[5] += med;
        colSums[6] += pol; colSums[7] += jpj; colSums[8] += misc; colSums[9] += typ;
        claimRows += `<tr>
          <td style="border:1px solid #999;padding:2px 2px;text-align:center;">${i + 1}</td>
          <td style="border:1px solid #999;padding:2px 2px;">${typeStr}</td>
          <td style="border:1px solid #999;padding:2px 2px;">${c.casefile?.id || ''}</td>
          <td style="border:1px solid #999;padding:2px 2px;">${c.casefile?.vehicleNo?.toUpperCase() || ''}</td>
          <td style="border:1px solid #999;padding:2px 2px;">${insName}</td>
          <td style="border:1px solid #999;padding:2px 2px;">${invNo}</td>
          <td style="border:1px solid #999;padding:2px 2px;text-align:right;">${this.fmt(invAmt)}</td>
          <td style="border:1px solid #999;padding:2px 2px;text-align:right;">${this.fmt(adjFees)}</td>
          <td style="border:1px solid #999;padding:2px 2px;">${c.casefile?.placeOfLoss || ''}</td>
          <td style="border:1px solid #999;padding:2px 2px;text-align:right;">${this.fmt(trav)}</td>
          <td style="border:1px solid #999;padding:2px 2px;text-align:right;">${this.fmt(add)}</td>
          <td style="border:1px solid #999;padding:2px 2px;text-align:right;">${this.fmt(sd)}</td>
          <td style="border:1px solid #999;padding:2px 2px;text-align:right;">${this.fmt(med)}</td>
          <td style="border:1px solid #999;padding:2px 2px;text-align:right;">${this.fmt(pol)}</td>
          <td style="border:1px solid #999;padding:2px 2px;text-align:right;">${this.fmt(jpj)}</td>
          <td style="border:1px solid #999;padding:2px 2px;text-align:right;">${this.fmt(misc)}</td>
          <td style="border:1px solid #999;padding:2px 2px;text-align:right;">${this.fmt(typ)}</td>
          <td style="border:1px solid #999;padding:2px 2px;text-align:right;font-weight:bold;">${this.fmt(total)}</td>
        </tr>`;
      });

      // SUB-TOTAL row
      const grandTotalCols = colSums[2]+colSums[3]+colSums[4]+colSums[5]+colSums[6]+colSums[7]+colSums[8]+colSums[9];
      const subTotalRow = claims.length ? `<tr style="font-weight:bold;background:#FCE4D6;">
        <td colspan="6" style="border:1px solid #999;padding:2px 2px;text-align:right;">SUB-TOTAL (Section A)</td>
        <td style="border:1px solid #999;padding:2px 2px;text-align:right;">${this.fmt(colSums[0])}</td>
        <td style="border:1px solid #999;padding:2px 2px;text-align:right;">${this.fmt(colSums[1])}</td>
        <td style="border:1px solid #999;padding:2px 2px;"></td>
        <td style="border:1px solid #999;padding:2px 2px;text-align:right;">${this.fmt(colSums[2])}</td>
        <td style="border:1px solid #999;padding:2px 2px;text-align:right;">${this.fmt(colSums[3])}</td>
        <td style="border:1px solid #999;padding:2px 2px;text-align:right;">${this.fmt(colSums[4])}</td>
        <td style="border:1px solid #999;padding:2px 2px;text-align:right;">${this.fmt(colSums[5])}</td>
        <td style="border:1px solid #999;padding:2px 2px;text-align:right;">${this.fmt(colSums[6])}</td>
        <td style="border:1px solid #999;padding:2px 2px;text-align:right;">${this.fmt(colSums[7])}</td>
        <td style="border:1px solid #999;padding:2px 2px;text-align:right;">${this.fmt(colSums[8])}</td>
        <td style="border:1px solid #999;padding:2px 2px;text-align:right;">${this.fmt(colSums[9])}</td>
        <td style="border:1px solid #999;padding:2px 2px;text-align:right;">${this.fmt(grandTotalCols)}</td>
      </tr>` : '';
      const accom = parseFloat((secC.find(r => r.rowType === 'accomodation') || {}).amount || 0);
      const phone = parseFloat((secC.find(r => r.rowType === 'handphone') || {}).amount || 0);
      const parking = parseFloat((secC.find(r => r.rowType === 'parking') || {}).amount || 0);
      let specify = 0;
      secC.filter(r => r.rowType && r.rowType.startsWith('specify')).forEach(r => {
        specify += parseFloat(r.amount || 0);
      });
      const advance = parseFloat((secC.find(r => r.rowType === 'advance') || {}).amount || 0);
      const subTotal = this.calcSubTotal(claims, secB);
      const amountPayable = Math.max(0, subTotal + accom + phone + parking + specify - advance);

      return `
        <div id="report-container" style="font-family:Arial,Helvetica,sans-serif;font-size:9px;padding:10px;color:#000;position:relative;">
          ${statusStamp}
          <div style="text-align:center;margin-bottom:10px;">
            <div style="font-size:14px;font-weight:bold;margin:0;">ASSOCIATED ADJUSTERS SDN BHD</div>
            <div style="font-size:11px;font-weight:bold;">MONTHLY REIMBURSEMENT CLAIMS</div>
            <div style="font-size:10px;margin:2px 0;">NAME: ${adj} — MONTH: ${group.month}</div>
          </div>

          <h3 style="font-size:11px;border-bottom:2px solid #333;padding-bottom:3px;margin:10px 0 4px;font-weight:bold;">SECTION A — INITIAL CLAIMS</h3>
          <table style="width:100%;border-collapse:collapse;margin-bottom:8px;font-size:7px;table-layout:fixed;">
            <tr><th style="border:1px solid #999;padding:1px 2px;background:#e0e0e0;text-align:center;font-weight:bold;">NO.</th><th style="border:1px solid #999;padding:1px 2px;background:#e0e0e0;text-align:center;font-weight:bold;">TYPE</th><th style="border:1px solid #999;padding:1px 2px;background:#e0e0e0;text-align:center;font-weight:bold;">FILE NO.</th><th style="border:1px solid #999;padding:1px 2px;background:#e0e0e0;text-align:center;font-weight:bold;">VEHICLE</th><th style="border:1px solid #999;padding:1px 2px;background:#e0e0e0;text-align:center;font-weight:bold;">INSURER</th><th style="border:1px solid #999;padding:1px 2px;background:#e0e0e0;text-align:center;font-weight:bold;">INV NO.</th><th style="border:1px solid #999;padding:1px 2px;background:#e0e0e0;text-align:center;font-weight:bold;">INV AMT</th><th style="border:1px solid #999;padding:1px 2px;background:#e0e0e0;text-align:center;font-weight:bold;">ADJ FEES</th><th style="border:1px solid #999;padding:1px 2px;background:#e0e0e0;text-align:center;font-weight:bold;">PLACE</th><th style="border:1px solid #999;padding:1px 2px;background:#e0e0e0;text-align:center;font-weight:bold;">TRAVEL</th><th style="border:1px solid #999;padding:1px 2px;background:#e0e0e0;text-align:center;font-weight:bold;">ADD.</th><th style="border:1px solid #999;padding:1px 2px;background:#e0e0e0;text-align:center;font-weight:bold;">SD & STAMPS</th><th style="border:1px solid #999;padding:1px 2px;background:#e0e0e0;text-align:center;font-weight:bold;">MEDICAL</th><th style="border:1px solid #999;padding:1px 2px;background:#e0e0e0;text-align:center;font-weight:bold;">POLICE</th><th style="border:1px solid #999;padding:1px 2px;background:#e0e0e0;text-align:center;font-weight:bold;">JPJ</th><th style="border:1px solid #999;padding:1px 2px;background:#e0e0e0;text-align:center;font-weight:bold;">MISC</th><th style="border:1px solid #999;padding:1px 2px;background:#e0e0e0;text-align:center;font-weight:bold;">TYPING</th><th style="border:1px solid #999;padding:1px 2px;background:#e0e0e0;text-align:center;font-weight:bold;">TOTAL</th></tr>
            ${claimRows || '<tr><td colspan="18" style="text-align:center;">No claims</td></tr>'}
            ${subTotalRow}
          </table>

          <h3 style="font-size:11px;border-bottom:2px solid #333;padding-bottom:3px;margin:10px 0 4px;font-weight:bold;">SECTION B — FOLLOW UP / SHARED FILES</h3>
          <table style="width:100%;border-collapse:collapse;margin-bottom:8px;font-size:7px;table-layout:fixed;">
            <tr><th style="border:1px solid #999;padding:1px 2px;background:#e0e0e0;text-align:center;font-weight:bold;">NO.</th><th style="border:1px solid #999;padding:1px 2px;background:#e0e0e0;text-align:center;font-weight:bold;">TYPE</th><th style="border:1px solid #999;padding:1px 2px;background:#e0e0e0;text-align:center;font-weight:bold;">FILE NO.</th><th style="border:1px solid #999;padding:1px 2px;background:#e0e0e0;text-align:center;font-weight:bold;">VEHICLE</th><th style="border:1px solid #999;padding:1px 2px;background:#e0e0e0;text-align:center;font-weight:bold;">INSURER</th><th style="border:1px solid #999;padding:1px 2px;background:#e0e0e0;text-align:center;font-weight:bold;">INV NO.</th><th style="border:1px solid #999;padding:1px 2px;background:#e0e0e0;text-align:center;font-weight:bold;">INV AMT</th><th style="border:1px solid #999;padding:1px 2px;background:#e0e0e0;text-align:center;font-weight:bold;">ADJ FEES</th><th style="border:1px solid #999;padding:1px 2px;background:#e0e0e0;text-align:center;font-weight:bold;">PLACE</th><th style="border:1px solid #999;padding:1px 2px;background:#e0e0e0;text-align:center;font-weight:bold;">TRAVEL</th><th style="border:1px solid #999;padding:1px 2px;background:#e0e0e0;text-align:center;font-weight:bold;">ADD.</th><th style="border:1px solid #999;padding:1px 2px;background:#e0e0e0;text-align:center;font-weight:bold;">SD & STAMPS</th><th style="border:1px solid #999;padding:1px 2px;background:#e0e0e0;text-align:center;font-weight:bold;">MEDICAL</th><th style="border:1px solid #999;padding:1px 2px;background:#e0e0e0;text-align:center;font-weight:bold;">POLICE</th><th style="border:1px solid #999;padding:1px 2px;background:#e0e0e0;text-align:center;font-weight:bold;">JPJ</th><th style="border:1px solid #999;padding:1px 2px;background:#e0e0e0;text-align:center;font-weight:bold;">MISC</th><th style="border:1px solid #999;padding:1px 2px;background:#e0e0e0;text-align:center;font-weight:bold;">TYPING</th><th style="border:1px solid #999;padding:1px 2px;background:#e0e0e0;text-align:center;font-weight:bold;">TOTAL</th></tr>
            ${(() => {
              let bSums = [0,0,0,0,0,0,0,0,0,0]; // invAmt, adjFees, trav, add, sd, med, pol, jpj, misc, typ
              const bRows = (secB || []).map((r, i) => {
                bSums[0] += parseFloat(r.invAmount || 0);
                bSums[1] += parseFloat(r.adjFees || 0);
                bSums[2] += parseFloat(r.travMileage || 0);
                bSums[3] += parseFloat(r.additional || 0);
                bSums[4] += parseFloat(r.sdAndStamps || 0);
                bSums[5] += parseFloat(r.medicalReport || 0);
                bSums[6] += parseFloat(r.policeDoc || 0);
                bSums[7] += parseFloat(r.jpjDoc || 0);
                bSums[8] += parseFloat(r.misc || 0);
                bSums[9] += parseFloat(r.typing || 0);
                return `<tr>
                  <td style="border:1px solid #999;padding:1px 2px;text-align:center;">${i + 1}</td>
                  <td style="border:1px solid #999;padding:1px 2px;">${r.type || ''}</td>
                  <td style="border:1px solid #999;padding:1px 2px;">${r.fileNo || ''}</td>
                  <td style="border:1px solid #999;padding:1px 2px;">${r.vehicleNo || ''}</td>
                  <td style="border:1px solid #999;padding:1px 2px;">${r.insurer || ''}</td>
                  <td style="border:1px solid #999;padding:1px 2px;">${r.invoiceNo || ''}</td>
                  <td style="border:1px solid #999;padding:1px 2px;text-align:right;">${this.fmt(r.invAmount)}</td>
                  <td style="border:1px solid #999;padding:1px 2px;text-align:right;">${this.fmt(r.adjFees)}</td>
                  <td style="border:1px solid #999;padding:1px 2px;">${r.placeOfLoss || ''}</td>
                  <td style="border:1px solid #999;padding:1px 2px;text-align:right;">${this.fmt(r.travMileage)}</td>
                  <td style="border:1px solid #999;padding:1px 2px;text-align:right;">${this.fmt(r.additional)}</td>
                  <td style="border:1px solid #999;padding:1px 2px;text-align:right;">${this.fmt(r.sdAndStamps)}</td>
                  <td style="border:1px solid #999;padding:1px 2px;text-align:right;">${this.fmt(r.medicalReport)}</td>
                  <td style="border:1px solid #999;padding:1px 2px;text-align:right;">${this.fmt(r.policeDoc)}</td>
                  <td style="border:1px solid #999;padding:1px 2px;text-align:right;">${this.fmt(r.jpjDoc)}</td>
                  <td style="border:1px solid #999;padding:1px 2px;text-align:right;">${this.fmt(r.misc)}</td>
                  <td style="border:1px solid #999;padding:1px 2px;text-align:right;">${this.fmt(r.typing)}</td>
                  <td style="border:1px solid #999;padding:1px 2px;text-align:right;font-weight:bold;">${this.fmt(this.rowTotalNum(r))}</td>
                </tr>`;
              }).join('');
              // Combined GRAND TOTAL (A + B)
              const gSums = [0,0,0,0,0,0,0,0,0,0];
              for (let i = 0; i < 10; i++) gSums[i] = colSums[i] + bSums[i];
              const gTotal = gSums.slice(2).reduce((a,b) => a + b, 0);
              const grandRow = (claims.length || secB.length) ? `<tr style="font-weight:bold;background:#FCE4D6;">
                <td colspan="6" style="border:1px solid #999;padding:1px 2px;text-align:right;">GRAND TOTAL (A + B)</td>
                <td style="border:1px solid #999;padding:1px 2px;text-align:right;">${this.fmt(gSums[0])}</td>
                <td style="border:1px solid #999;padding:1px 2px;text-align:right;">${this.fmt(gSums[1])}</td>
                <td style="border:1px solid #999;padding:1px 2px;"></td>
                <td style="border:1px solid #999;padding:1px 2px;text-align:right;">${this.fmt(gSums[2])}</td>
                <td style="border:1px solid #999;padding:1px 2px;text-align:right;">${this.fmt(gSums[3])}</td>
                <td style="border:1px solid #999;padding:1px 2px;text-align:right;">${this.fmt(gSums[4])}</td>
                <td style="border:1px solid #999;padding:1px 2px;text-align:right;">${this.fmt(gSums[5])}</td>
                <td style="border:1px solid #999;padding:1px 2px;text-align:right;">${this.fmt(gSums[6])}</td>
                <td style="border:1px solid #999;padding:1px 2px;text-align:right;">${this.fmt(gSums[7])}</td>
                <td style="border:1px solid #999;padding:1px 2px;text-align:right;">${this.fmt(gSums[8])}</td>
                <td style="border:1px solid #999;padding:1px 2px;text-align:right;">${this.fmt(gSums[9])}</td>
                <td style="border:1px solid #999;padding:1px 2px;text-align:right;">${this.fmt(gTotal)}</td>
              </tr>` : '';
              return (bRows || '<tr><td colspan="18" style="text-align:center;">No follow up claims</td></tr>') + grandRow;
            })()}
          </table>

          <h3 style="font-size:11px;border-bottom:2px solid #333;padding-bottom:3px;margin:10px 0 4px;font-weight:bold;">SECTION C — DYNAMIC CLAIMS</h3>
          <table style="width:100%;border-collapse:collapse;margin-bottom:8px;font-size:7px;page-break-inside:avoid;">
            <tr><th style="border:1px solid #999;padding:1px 2px;background:#e0e0e0;font-weight:bold;text-align:right;">Description</th><th style="border:1px solid #999;padding:1px 2px;background:#e0e0e0;font-weight:bold;text-align:right;">Amount (RM)</th></tr>
            <tr><td style="border:1px solid #999;padding:1px 2px;font-weight:bold;text-align:right;">SUB - TOTAL</td><td style="border:1px solid #999;padding:1px 2px;text-align:right;font-weight:bold;background:#FCE4D6;">${this.fmt(subTotal)}</td></tr>
            <tr><td style="border:1px solid #999;padding:1px 2px;text-align:right;">ACCOMODATION${accomRow.rate ? ` (RM ${this.fmtRaw(accomRow.rate)} X ${this.fmtRaw(accomRow.days || 0)} DAYS)` : ''}</td><td style="border:1px solid #999;padding:1px 2px;text-align:right;background:#FCE4D6;">${this.fmt(accom)}</td></tr>
            <tr><td style="border:1px solid #999;padding:1px 2px;text-align:right;">HANDPHONE BILL</td><td style="border:1px solid #999;padding:1px 2px;text-align:right;background:#FCE4D6;">${this.fmt(phone)}</td></tr>
            <tr><td style="border:1px solid #999;padding:1px 2px;text-align:right;">MONTHLY PARKING FEES</td><td style="border:1px solid #999;padding:1px 2px;text-align:right;background:#FCE4D6;">${this.fmt(parking)}</td></tr>
            ${secC.filter(r => r.rowType && r.rowType.startsWith('specify') && (r.label || parseFloat(r.amount || 0) > 0)).map(r => `<tr><td style="border:1px solid #999;padding:1px 2px;text-align:right;">${r.label || '—'}</td><td style="border:1px solid #999;padding:1px 2px;text-align:right;background:#FCE4D6;">${this.fmt(r.amount || 0)}</td></tr>`).join('')}
            <tr><td style="border:1px solid #999;padding:1px 2px;text-align:right;">(LESS) TRAVELLING ADVANCE</td><td style="border:1px solid #999;padding:1px 2px;text-align:right;background:#FCE4D6;">${this.fmt(advance)}</td></tr>
            <tr style="font-weight:bold;"><td style="border:1px solid #999;padding:1px 2px;text-align:right;">AMOUNT PAYABLE</td><td style="border:1px solid #999;padding:1px 2px;text-align:right;background:#FCE4D6;">${this.fmt(amountPayable)}</td></tr>
          </table>

          <div style="margin-top:15px;page-break-inside:avoid;">
            <h3 style="font-size:11px;border-bottom:2px solid #333;padding-bottom:3px;font-weight:bold;">APPROVED BY</h3>
            <table style="border-collapse:collapse;margin-top:3px;">
              <tr><td style="padding:2px 10px;border:none;">Level 1 — Manager</td><td style="padding:2px 10px;border:none;">${mgrName}</td><td style="padding:2px 10px;border:none;">${group.managerApprovedAt ? moment(group.managerApprovedAt).format('DD/MM/YYYY') : '—'}</td></tr>
              <tr><td style="padding:2px 10px;border:none;">Level 2 — Payroll Clerk</td><td style="padding:2px 10px;border:none;">${clerkName}</td><td style="padding:2px 10px;border:none;">${group.payrollClerkApprovedAt ? moment(group.payrollClerkApprovedAt).format('DD/MM/YYYY') : '—'}</td></tr>
              <tr><td style="padding:2px 10px;border:none;">Level 3 — Payroll Manager</td><td style="padding:2px 10px;border:none;">${pmName}</td><td style="padding:2px 10px;border:none;">${group.payrollManagerApprovedAt ? moment(group.payrollManagerApprovedAt).format('DD/MM/YYYY') : '—'}</td></tr>
            </table>
          </div>
        </div>`;
},
	    claimTotalNum(claim) {
      if (!claim) return 0;
      return parseFloat(claim.travMileage || 0) + parseFloat(claim.additional || 0) +
        parseFloat(claim.sdAndStamps || 0) + parseFloat(claim.medicalReport || 0) +
        parseFloat(claim.policeDoc || 0) + parseFloat(claim.jpjDoc || 0) +
        parseFloat(claim.misc || 0) + parseFloat(claim.typing || 0);
    },
    rowTotalNum(row) {
      if (!row) return 0;
      return parseFloat(row.travMileage || 0) + parseFloat(row.additional || 0) +
        parseFloat(row.sdAndStamps || 0) + parseFloat(row.medicalReport || 0) +
        parseFloat(row.policeDoc || 0) + parseFloat(row.jpjDoc || 0) +
        parseFloat(row.misc || 0) + parseFloat(row.typing || 0);
    },
    calcSubTotal(claims, secB) {
      let total = 0;
      (claims || []).forEach(c => { total += this.claimTotalNum(c); });
      (secB || []).forEach(r => { total += this.rowTotalNum(r); });
      return total;
    },
statusColor(status) {
      const colors = {
        "PRE-DRAFT": "grey",
        "DRAFT": "grey", 
        "MANAGER_REVIEW": "orange", 
        "MANAGER_APPROVED": "blue",
        "PAYROLL_CLERK_REVIEW": "purple", 
        "PAYROLL_CLERK_APPROVED": "indigo",
        "PAYROLL_MANAGER_REVIEW": "deep-purple", 
        "PAID": "green",
        "QUERIED": "warning", 
        "REJECTED": "error",
        "PENDING": "brown",
      };
      return colors[status] || "grey";
    },
    fmt(value) {
      return parseFloat(value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    fmtRaw(value) {
      return parseFloat(value || 0).toFixed(2);
    },
    formatShortDate(value) {
      return value ? moment(value).format('DD/MM/YYYY') : '';
    },
    formatDateTime(value) {
      return value ? moment(value).format('DD/MM/YYYY - hh.mma') : '';
    },
  },
  async mounted() {
    await this.$store.dispatch("referenceData/ensureRefs");
    // Set default month range to previous month (current active claims)
    const now = new Date();
    const prevMonth = now.getMonth() === 0 ? 11 : now.getMonth() - 1;
    const prevYear = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
    const pad = (n) => String(n).padStart(2, '0');
    this.filterMonthFrom = `${prevYear}-${pad(prevMonth + 1)}`;
    this.filterMonthTo = `${prevYear}-${pad(prevMonth + 1)}`;

    // Load insurer and subRefTypes for report generation
    try {
      const [insRes, subRes] = await Promise.all([
        InsDataService.getAll(),
        SubDeptDataService.getAll(),
      ]);
      this.insurer = insRes.data || [];
      this.subRefTypes = subRes.data || [];
    } catch (e) {
      console.error("Failed to load reference data", e);
    }
    // Load branches with role scoping (same pattern as dashboard)
    const user = this.$store.state.auth.user || {};
    const roles = Array.isArray(user.roles) ? user.roles : [];
    const isAdminOrManager = roles.includes("ADMIN") || roles.includes("MANAGER") || roles.includes("PAYROLL_MANAGER");
    let branches = this.$store.state.referenceData.branches || [];
    if (!isAdminOrManager) {
      const userBranches = Array.isArray(user.branches) ? user.branches : [];
      branches = branches.filter(x => userBranches.includes(x.id));
    }
    this.branches = branches
      .map(b => ({ ...b, name: b.name?.toUpperCase() || '' }))
      .sort((a, b) => a.name?.localeCompare(b.name || ''));
    this.fetchData();
  },
};
</script>

<style scoped>
.report-preview {
  padding: 20px;
  background: #fff;
  overflow: auto;
  height: calc(100vh - 64px);
}
</style>
